import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateId } from '../utils/helpers';
import { useCRM } from './CRMContext';
import { useTenant } from './TenantContext';
import { supabase } from '../supabase/supabase';

const LinkedInContext = createContext();

export const useLinkedIn = () => useContext(LinkedInContext);

const normalizeLinkedInUrl = (value) => {
  try {
    const url = new URL(value.trim());
    const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
    const pathname = url.pathname.replace(/\/+$/, '').toLowerCase();

    return `https://${hostname}${pathname}`;
  } catch {
    return typeof value === 'string' ? value.trim() : '';
  }
};

const isValidLinkedInUrl = (value) => {
  try {
    const url = new URL(value.trim());
    const hostname = url.hostname.toLowerCase();
    const pathname = url.pathname.replace(/\/+$/, '');

    return (
      url.protocol === 'https:' &&
      (hostname === 'linkedin.com' || hostname === 'www.linkedin.com') &&
      pathname.toLowerCase().startsWith('/in/') &&
      pathname.length > 4
    );
  } catch {
    return false;
  }
};

const RESEARCH_NOTE = 'Finn relevant kontaktperson på LinkedIn';

const buildLinkedInSearchUrl = (companyName) => {
  const roles = [
    'Daglig leder',
    'CEO',
    'HR',
    'HR-leder',
    'HMS',
    'HSE',
    'COO',
    'Operations'
  ];

  return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(
    `${companyName} ${roles.join(' ')}`
  )}`;
};

export const LinkedInProvider = ({ children }) => {
  const { currentUser } = useCRM();
  const { activeTenantId } = useTenant();

  // --- STATE ---
  const [prospects, setProspects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [stats, setStats] = useState({ todayCount: 0, dailyLimit: 40 });

  // --- MOCK DATA LOADER ---
  useEffect(() => {
    // Load some mock data for visualization
    const mockProspects = [
      { id: 'p1', full_name: 'Jens Tech', title: 'CTO', company_name: 'Tech Nordic', linkedin_url: 'https://linkedin.com/in/example', status: 'Ny', priority: 'Høy' },
      { id: 'p2', full_name: 'Kari Salg', title: 'Salgssjef', company_name: 'Vekst AS', linkedin_url: 'https://linkedin.com/in/example2', status: 'Kontaktet', priority: 'Medium' },
      { id: 'p3', full_name: 'Per Innkjøp', title: 'CFO', company_name: 'Solid Drift', linkedin_url: 'https://linkedin.com/in/example3', status: 'Dialog', priority: 'Høy' },
    ];
    
    // Default Sequence
    const defaultSequence = {
      id: 'seq-1',
      name: 'Standard B2B Intro',
      steps: [
        { step: 1, type: 'Connect', delay: 0, template: "Hei {{FirstName}}, ser vi jobber mot samme bransje. Hadde vært hyggelig å knytte kontakt her på LinkedIn." },
        { step: 2, type: 'Message', delay: 2, template: "Hei igjen {{FirstName}}. Takk for connect. Jeg ser dere vokser innen {{Company}}. Vi hjelper lignende selskaper med X. Er dette relevant for deg?" },
        { step: 3, type: 'Message', delay: 4, template: "Hei {{FirstName}}, ville bare løfte denne. Har du 5 minutter til en kaffeprat neste uke?" }
      ]
    };

    // Generate Tasks based on prospects
    const mockTasks = [
      { id: 't1', prospect_id: 'p1', type: 'Connect', status: 'Åpen', due_date: new Date().toISOString().split('T')[0], template_text: defaultSequence.steps[0].template },
      { id: 't2', prospect_id: 'p2', type: 'Message', status: 'Åpen', due_date: new Date().toISOString().split('T')[0], template_text: defaultSequence.steps[1].template },
    ];

    const demoMode = false;
    if (demoMode) {
      setProspects(mockProspects);
      setTasks(mockTasks);
    }

    let mounted = true;

    const loadData = async () => {
      if (!activeTenantId) {
        if (import.meta.env.DEV) {
          console.warn('[LinkedIn] Mangler activeTenantId. Outreach-data lastes ikke uten tenant-scope.');
        }

        setProspects([]);
        setTasks([]);
        setSequences([]);
        return;
      }

      const settledResults = await Promise.allSettled([
        supabase
          .from('li_prospects')
          .select('*')
          .eq('tenant_id', activeTenantId)
          .order('created_at', { ascending: false }),
        supabase
          .from('li_tasks')
          .select('*')
          .eq('tenant_id', activeTenantId)
          .order('due_date', { ascending: true }),
        supabase
          .from('li_sequences')
          .select('*')
          .eq('tenant_id', activeTenantId)
          .order('created_at', { ascending: true })
      ]);

      const [prospectsResult, tasksResult, sequencesResult] = settledResults.map(result => (
        result.status === 'fulfilled'
          ? result.value
          : {
              data: null,
              error: result.reason instanceof Error
                ? result.reason
                : new Error('Supabase-forespørselen feilet.')
            }
      ));

      if (!mounted) return;

      const error = prospectsResult.error || tasksResult.error || sequencesResult.error;

      if (error) {
        console.error('[LinkedIn] Kunne ikke laste outreach-data:', error.message);
        if (prospectsResult.error) setProspects([]);
        if (tasksResult.error) setTasks([]);
        if (sequencesResult.error) setSequences([defaultSequence]);
        if (!prospectsResult.error) setProspects(prospectsResult.data || []);
        if (!tasksResult.error) setTasks(tasksResult.data || []);
        if (!sequencesResult.error) setSequences(sequencesResult.data?.length ? sequencesResult.data : [defaultSequence]);
        return;
      }

      setProspects(prospectsResult.data || []);
      setTasks(tasksResult.data || []);
      setSequences(sequencesResult.data?.length ? sequencesResult.data : [defaultSequence]);
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [activeTenantId]);

  // --- ACTIONS ---

  const addProspect = (prospect) => {
    const newProspect = { ...prospect, id: generateId('li_pros'), status: 'Ny', created_at: new Date().toISOString() };
    const rawUrl = prospect.linkedin_url || '';

    return (async () => {
      try {
        if (!activeTenantId) {
          if (import.meta.env.DEV) {
            console.warn('[LinkedIn] Prospekt kan ikke lagres uten activeTenantId.');
          }

          return { success: false, error: 'Prospektet kan ikke lagres før organisasjonen er valgt.' };
        }

        if (!prospect?.full_name?.trim()) {
          return { success: false, error: 'Navn er påkrevd.' };
        }

        if (!isValidLinkedInUrl(rawUrl)) {
          return { success: false, error: 'Skriv inn en gyldig LinkedIn-profil-URL.' };
        }

        const normalizedUrl = normalizeLinkedInUrl(rawUrl);

        const { data: duplicate, error: duplicateError } = await supabase
          .from('li_prospects')
          .select('id')
          .eq('tenant_id', activeTenantId)
          .eq('linkedin_url', normalizedUrl)
          .maybeSingle();

        if (duplicateError && duplicateError.code !== 'PGRST116') {
          console.warn('[LINKEDIN DUPLICATE CHECK ERROR]', {
            code: duplicateError?.code,
            message: duplicateError?.message
          });
          return { success: false, error: 'Kunne ikke kontrollere duplikat. Sjekk Supabase/RLS.' };
        }

        if (duplicate) {
          return { success: false, error: 'Prospektet finnes allerede.' };
        }

        const prospectPayload = {
          linkedin_url: normalizedUrl,
          full_name: newProspect.full_name.trim(),
          title: newProspect.title?.trim() || '',
          company_name: newProspect.company_name?.trim() || '',
          priority: newProspect.priority || 'Medium',
          notes: newProspect.notes?.trim() || '',
          status: 'Ny',
          owner_user_id: currentUser?.id || null,
          created_at: newProspect.created_at,
          tenant_id: activeTenantId
        };

        const { data: savedProspect, error: prospectError } = await supabase
          .from('li_prospects')
          .insert(prospectPayload)
          .select()
          .single();

        if (prospectError) {
          console.error('[LinkedIn] Prospekt kunne ikke lagres:', prospectError.message);
          return { success: false, error: 'Prospektet kunne ikke lagres.' };
        }

        setProspects(prev => [savedProspect, ...prev]);
      
        // Auto-generate first task if sequence selected
        if (sequences.length > 0) {
          const firstStep = sequences[0].steps[0];
          const newTask = {
            id: generateId('li_task'),
            prospect_id: savedProspect.id,
            type: firstStep.type,
            status: 'Åpen',
            due_date: new Date().toISOString().split('T')[0],
            template_text: firstStep.template
          };

          const taskPayload = {
            prospect_id: newTask.prospect_id,
            sequence_id: sequences[0].id?.startsWith('seq-') ? null : sequences[0].id,
            type: newTask.type,
            status: newTask.status,
            due_date: newTask.due_date,
            template_text: newTask.template_text,
            owner_user_id: currentUser?.id || null,
            tenant_id: activeTenantId
          };

          const { data: savedTask, error: taskError } = await supabase
            .from('li_tasks')
            .insert(taskPayload)
            .select()
            .single();

          if (taskError) {
            console.error('[LinkedIn] Første oppgave kunne ikke lagres:', taskError.message);
            return { success: false, prospect: savedProspect, error: 'Prospektet ble lagret, men første oppgave kunne ikke opprettes.' };
          }

          setTasks(prev => [...prev, savedTask]);
          return { success: true, prospect: savedProspect, task: savedTask };
        }

        return { success: true, prospect: savedProspect, task: null };
      } catch (error) {
        console.error('[LinkedIn] Prospekt kunne ikke behandles:', error.message);
        return { success: false, error: 'Prospektet kunne ikke behandles.' };
      }
    })();
  };

  const createCompanyResearchProspect = ({ companyName, orgNumber = '' }) => {
    const normalizedCompanyName = String(companyName || '').trim();

    if (!normalizedCompanyName) {
      return Promise.resolve({
        success: false,
        error: 'Selskapsnavn mangler.'
      });
    }

    if (!activeTenantId) {
      if (import.meta.env.DEV) {
        console.warn('[LinkedIn] Research-prospect kan ikke lagres uten activeTenantId.');
      }

      return Promise.resolve({
        success: false,
        error: 'Prospektet kan ikke lagres før organisasjonen er valgt.'
      });
    }

    const linkedinSearchUrl = buildLinkedInSearchUrl(normalizedCompanyName);
    const orgNote = String(orgNumber || '').trim();
    const notes = [
      RESEARCH_NOTE,
      orgNote ? `BRREG orgnr: ${orgNote}` : ''
    ].filter(Boolean).join('\n');

    return (async () => {
      try {
        const { data: duplicate, error: duplicateError } = await supabase
          .from('li_prospects')
          .select('id')
          .eq('tenant_id', activeTenantId)
          .eq('company_name', normalizedCompanyName)
          .eq('title', 'Research')
          .eq('notes', notes)
          .maybeSingle();

        if (duplicateError && duplicateError.code !== 'PGRST116') {
          console.warn('[LINKEDIN RESEARCH DUPLICATE CHECK ERROR]', {
            code: duplicateError?.code,
            message: duplicateError?.message
          });
          return {
            success: false,
            error: 'Kunne ikke kontrollere duplikat. Sjekk Supabase/RLS.'
          };
        }

        if (duplicate) {
          return {
            success: false,
            duplicate: true,
            error: 'LinkedIn-prospect finnes allerede'
          };
        }

        const createdAt = new Date().toISOString();
        const prospectPayload = {
          linkedin_url: linkedinSearchUrl,
          full_name: 'Finn kontaktperson',
          title: 'Research',
          company_name: normalizedCompanyName,
          priority: 'Medium',
          notes,
          status: 'Ny',
          owner_user_id: currentUser?.id || null,
          created_at: createdAt,
          tenant_id: activeTenantId
        };

        const { data: savedProspect, error: prospectError } = await supabase
          .from('li_prospects')
          .insert(prospectPayload)
          .select()
          .single();

        if (prospectError) {
          console.error('[LinkedIn] Research-prospect kunne ikke lagres:', prospectError.message);
          return {
            success: false,
            error: 'Research-prospectet kunne ikke lagres.'
          };
        }

        const taskPayload = {
          prospect_id: savedProspect.id,
          sequence_id: null,
          type: 'Research',
          status: 'Åpen',
          due_date: new Date().toISOString().split('T')[0],
          template_text: RESEARCH_NOTE,
          owner_user_id: currentUser?.id || null,
          tenant_id: activeTenantId
        };

        const { data: savedTask, error: taskError } = await supabase
          .from('li_tasks')
          .insert(taskPayload)
          .select()
          .single();

        if (taskError) {
          console.error('[LinkedIn] Research-oppgaven kunne ikke lagres:', taskError.message);
          return {
            success: false,
            prospect: savedProspect,
            error: 'Prospektet ble lagret, men research-oppgaven kunne ikke opprettes.'
          };
        }

        setProspects(prev => [savedProspect, ...prev]);
        setTasks(prev => [...prev, savedTask]);

        return {
          success: true,
          prospect: savedProspect,
          task: savedTask
        };
      } catch (error) {
        console.error('[LinkedIn] Research-prospect kunne ikke behandles:', error.message);
        return {
          success: false,
          error: 'Research-prospectet kunne ikke behandles.'
        };
      }
    })();
  };

  const updateProspectStatus = (id, status) => {
    return (async () => {
      if (!activeTenantId) {
        if (import.meta.env.DEV) {
          console.warn('[LinkedIn] Prospektstatus kan ikke oppdateres uten activeTenantId.');
        }

        return { error: 'Prospektstatus kan ikke oppdateres uten valgt organisasjon.' };
      }

      const { error } = await supabase
        .from('li_prospects')
        .update({ status })
        .eq('id', id)
        .eq('tenant_id', activeTenantId);

      if (error) {
        console.error('[LinkedIn] Status kunne ikke oppdateres:', error.message);
        return { error: 'Status kunne ikke oppdateres.' };
      }

      setProspects(prev => prev.map(p => p.id === id ? { ...p, status } : p));
      return { success: true };
    })();
  };

  const updateProspect = (id, updates) => {
    return (async () => {
      if (!activeTenantId) {
        if (import.meta.env.DEV) {
          console.warn('[LinkedIn] Prospektet kan ikke oppdateres uten activeTenantId.');
        }

        return { success: false, error: 'Prospektet kan ikke oppdateres uten valgt organisasjon.' };
      }

      const payload = {
        full_name: String(updates.full_name || '').trim() || 'Finn kontaktperson',
        title: String(updates.title || '').trim(),
        company_name: String(updates.company_name || '').trim(),
        linkedin_url: String(updates.linkedin_url || '').trim() || null,
        priority: updates.priority || 'Medium',
        notes: String(updates.notes || '').trim(),
        status: updates.status || 'Ny'
      };

      const { data, error } = await supabase
        .from('li_prospects')
        .update(payload)
        .eq('id', id)
        .eq('tenant_id', activeTenantId)
        .select()
        .single();

      if (error) {
        console.error('[LinkedIn] Prospektet kunne ikke oppdateres:', error.message);
        return { success: false, error: 'Prospektet kunne ikke oppdateres.' };
      }

      setProspects(prev => prev.map(prospect => prospect.id === id ? data : prospect));
      return { success: true, prospect: data };
    })();
  };

  const ensureOutreachTask = (prospect) => {
    return (async () => {
      if (!activeTenantId) {
        if (import.meta.env.DEV) {
          console.warn('[LinkedIn] Oppgave kan ikke opprettes uten activeTenantId.');
        }

        return { success: false, error: 'Oppgaven kan ikke opprettes uten valgt organisasjon.' };
      }

      const { data: existingTasks, error: lookupError } = await supabase
        .from('li_tasks')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .eq('prospect_id', prospect.id)
        .eq('status', 'Åpen')
        .limit(1);

      if (lookupError) {
        console.error('[LinkedIn] Eksisterende oppgave kunne ikke kontrolleres:', lookupError.message);
        return { success: false, error: 'Kunne ikke kontrollere eksisterende oppgave.' };
      }

      if (existingTasks?.[0]) {
        return {
          success: false,
          duplicate: true,
          task: existingTasks[0],
          error: 'Oppgave finnes allerede'
        };
      }

      const taskPayload = {
        prospect_id: prospect.id,
        sequence_id: null,
        type: prospect.title === 'Research' ? 'Research' : 'Connect',
        status: 'Åpen',
        due_date: new Date().toISOString().split('T')[0],
        template_text: prospect.title === 'Research'
          ? RESEARCH_NOTE
          : 'Hei {{FirstName}}, jeg vil gjerne knytte kontakt her på LinkedIn.',
        owner_user_id: currentUser?.id || null,
        tenant_id: activeTenantId
      };

      const { data, error } = await supabase
        .from('li_tasks')
        .insert(taskPayload)
        .select()
        .single();

      if (error) {
        console.error('[LinkedIn] Outreach-oppgaven kunne ikke opprettes:', error.message);
        return { success: false, error: 'Outreach-oppgaven kunne ikke opprettes.' };
      }

      setTasks(prev => [...prev, data]);
      return { success: true, task: data };
    })();
  };

  const completeTask = (taskId, outcome) => {
    return (async () => {
      if (!activeTenantId) {
        if (import.meta.env.DEV) {
          console.warn('[LinkedIn] Oppgaven kan ikke fullføres uten activeTenantId.');
        }

        return { error: 'Oppgaven kan ikke fullføres uten valgt organisasjon.' };
      }

      // 1. Mark task complete
      const task = tasks.find(t => t.id === taskId);
      if (!task) return { error: 'Fant ikke oppgaven.' };

      const { error } = await supabase
        .from('li_tasks')
        .update({ status: 'Utført' })
        .eq('id', taskId)
        .eq('tenant_id', activeTenantId);

      if (error) {
        console.error('[LinkedIn] Oppgaven kunne ikke fullføres:', error.message);
        return { error: 'Oppgaven kunne ikke fullføres.' };
      }

      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'Utført' } : t));
      setStats(prev => ({ ...prev, todayCount: prev.todayCount + 1 }));

      // 2. Logic for next step (Simplified)
      // Find next step in sequence
      const seq = sequences.find(sequence => sequence.id === task.sequence_id) || sequences[0];
      if (!seq) return { success: true };

      const currentStepIndex = seq.steps.findIndex(s => s.type === task.type && s.template === task.template_text); // Rough matching
      
      if (currentStepIndex !== -1 && currentStepIndex < seq.steps.length - 1) {
        const nextStep = seq.steps[currentStepIndex + 1];
        const nextDate = new Date();
        nextDate.setDate(nextDate.getDate() + nextStep.delay);

        const { data: nextTask, error: nextTaskError } = await supabase
          .from('li_tasks')
          .insert({
            prospect_id: task.prospect_id,
            sequence_id: seq.id?.startsWith('seq-') ? null : seq.id,
            type: nextStep.type,
            status: 'Åpen',
            due_date: nextDate.toISOString().split('T')[0],
            template_text: nextStep.template,
            owner_user_id: currentUser?.id || null,
            tenant_id: activeTenantId
          })
          .select()
          .single();

        if (nextTaskError) {
          console.error('[LinkedIn] Neste oppgave kunne ikke lagres:', nextTaskError.message);
          return { error: 'Oppgaven ble fullført, men neste oppgave kunne ikke opprettes.' };
        }

        setTasks(prev => [...prev, nextTask]);
      }

      // 3. Update Prospect Status based on action
      if (task.type === 'Connect') await updateProspectStatus(task.prospect_id, 'Kontaktet');
      if (task.type === 'Message') await updateProspectStatus(task.prospect_id, 'Dialog');

      return { success: true };
    })();
  };

  const skipTask = (taskId) => {
    return (async () => {
      if (!activeTenantId) {
        if (import.meta.env.DEV) {
          console.warn('[LinkedIn] Oppgaven kan ikke hoppes over uten activeTenantId.');
        }

        return { error: 'Oppgaven kan ikke hoppes over uten valgt organisasjon.' };
      }

      const { error } = await supabase
        .from('li_tasks')
        .update({ status: 'Hoppet over' })
        .eq('id', taskId)
        .eq('tenant_id', activeTenantId);

      if (error) {
        console.error('[LinkedIn] Oppgaven kunne ikke hoppes over:', error.message);
        return { error: 'Oppgaven kunne ikke hoppes over.' };
      }

      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'Hoppet over' } : t));
      return { success: true };
    })();
  };

  // AI Mock Generator
  const generateAiIcebreaker = async (prospect) => {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(`Hei ${prospect.full_name.split(' ')[0]}, imponerende arbeid dere gjør i ${prospect.company_name}. Jeg ser vi har felles interesser innen teknologi.`);
      }, 1000);
    });
  };

  return (
    <LinkedInContext.Provider value={{
      prospects,
      tasks,
      sequences,
      stats,
      addProspect,
      createCompanyResearchProspect,
      updateProspectStatus,
      updateProspect,
      ensureOutreachTask,
      completeTask,
      skipTask,
      generateAiIcebreaker
    }}>
      {children}
    </LinkedInContext.Provider>
  );
};